import React, { useState, useContext, useEffect, useCallback } from 'react'
import { styled } from '@mui/material/styles'
import { Routes, Route } from 'react-router-dom'
import { useRetirementGapCalculations } from 'layouts/authenticated/pages/dashboard/hooks/useRetirementGapCalculations'
import { usePARCOScoreCalculations } from 'layouts/authenticated/pages/dashboard/hooks/usePARCOScoreCalculations'
import { UpdatedCalcs } from 'admin/UpdatedCalcs'
import { IntakeContext } from 'context/IntakeContext'
import { apiClient } from 'utils/api/apiClient'
import { useModal } from 'components/modals'
import { useFormDebounce } from 'hooks/useFormDebounce'

// Theme Components
import DashboardLayout from 'components/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'components/Navbars/DashboardNavbar'
// Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
// MUI Components
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  Grid,
  Card,
  Button,
} from '@mui/material'
// import RetirementGap from './components/newComponents/RetirementGap/RetirementGapCard'
// import RetirementGapProvider from './components/newComponents/RetirementGap/RetirementGapContext'
import AltruistOpenRoth from './components/newComponents/AltruistOpenRoth/AltruistOpenRoth'
import AltruistOpenRothProvider from './components/newComponents/AltruistOpenRoth/AltruistOpenRothContext'
import AltruistIndex from './components/newComponents/AltruistOpenRoth'
// import DailyUserCard from './components/newComponents/DailyUsers/DailyUserCards'
import { GifBoxOutlined } from '@mui/icons-material'
import colors from 'assets/theme/base/colors'

import { ReferralModal } from 'components/Referral'

import {
  format,
  differenceInYears,
  subYears,
  addYears,
  addDays,
  addMonths,
  max,
  min,
  differenceInMonths,
  subMonths,
} from 'date-fns'
import Referral from 'components/OnboardingReferral'
import AppointmentScheduler from 'components/AppointmentScheduler'
import QuilttConnectProvider from 'layouts/authenticated/pages/newdash/components/QuilttConnectProvider'
import SP500 from 'components/graphs/SP500'
import { SignalConnection } from 'components/SignalConnection'
import { AuditTimeline } from './components/AuditTimeline'
import WebinarSignup from 'components/WebinarSignup'

const dataset = [
  { year: 2014, actual: 11.39 },
  { year: 2015, actual: -0.73 },
  { year: 2016, actual: 9.54 },
  { year: 2017, actual: 19.42 },
  { year: 2018, actual: -6.24 },
  { year: 2019, actual: 28.88 },
  { year: 2020, actual: 16.26 },
  { year: 2021, actual: 26.89 },
  { year: 2022, actual: -19.44 },
  { year: 2023, actual: 24.23 },
  { year: 2024, actual: 23.31 },
]

const CustomAccordion = styled(Accordion)(({ theme }) => ({
  '&.MuiPaper-root': {
    display: 'flex',
    flexDirection: 'column-reverse',
  },
  '& .MuiAccordionSummary-root': {
    // flexDirection: 'row-reverse',
  },
  '& .MuiAccordionDetails-root': {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}))

export const NewComponentReference = () => {
  const {
    intakeState,
    updateUser,
    updateIntakeStateAtKey,
    userTasks,
    isAdmin,
  } = useContext(IntakeContext)
  const { updateIntakeStateAndSubmit } = useFormDebounce()
  const { openModal } = useModal()
  const [assets, setAssets] = useState([])
  const [liabilities, setLiabilities] = useState([])
  const [deductions, setDeductions] = useState([])
  const [modalOpen, setModalOpen] = useState(false)
  const getAccounts = useCallback(async () => {
    const {
      data: { items },
    } = await apiClient.get('api/assets')
    const foundAssets = items?.filter(({ type }) => type === 'asset') || []
    setAssets(foundAssets)
    const foundLiabilities =
      items?.filter(({ type }) => type === 'liability') || []
    setLiabilities(foundLiabilities)
    const foundDeductions =
      items?.filter(({ type }) => type === 'deduction') || []
    setDeductions(foundDeductions)
  }, [])

  const [openReferral, setOpenReferral] = useState(false)

  const handleReferralClose = () => {
    setOpenReferral(false)
  }

  const handleReferralOpen = () => {
    setOpenReferral(true)
  }

  useEffect(() => {
    let fetched = false
    if (fetched) return

    getAccounts()
    fetched = true
  }, [getAccounts])

  const { valueOfAssets, readinessScoreAdjustedMax } = UpdatedCalcs({
    assets,
    liabilities,
  })
  const {
    adjustedNetTakeHomeInRetirement,
    adjustedWorkingNetTakeHome,
    adjustedNetTakeHomeDifferencePARCOGap,
  } = useRetirementGapCalculations(deductions, liabilities)

  const { fngSafeIncomeAdvantageCost, northAmericanBenefitSolutionsCost } =
    usePARCOScoreCalculations(
      assets,
      liabilities,
      adjustedWorkingNetTakeHome,
      adjustedNetTakeHomeInRetirement,
      adjustedNetTakeHomeDifferencePARCOGap
    )

  const fngReference = fngSafeIncomeAdvantageCost({
    dateOfBirth: intakeState?.dateOfBirth,
    plannedRetirementDate: intakeState?.dateOfPlannedRetirement,
    grossIncomeGap: adjustedNetTakeHomeDifferencePARCOGap,
  })

  const naReference = northAmericanBenefitSolutionsCost({
    dateOfBirth: intakeState?.dateOfBirth,
    plannedRetirementDate: intakeState?.dateOfPlannedRetirement,
    grossIncomeGap: adjustedNetTakeHomeDifferencePARCOGap,
  })

  const dollarFormat = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })

  // console.log('userId', intakeState?.id)

  return (
    // <RetirementGapProvider>
    <AltruistOpenRothProvider>
      <DashboardLayout>
        <Box
          px={{
            xs: 4,
            sm: 4,
            md: 4,
            lg: 6,
            xl: 6,
            xxl: 6,
            test: 6,
            xl3: 6,
          }}
          pb={6}
        >
          <Grid item xs={12} mb={2} mt={6}>
            <Typography
              variant="h1"
              fontWeight="600"
              fontSize="28px"
              textAlign="left"
              color="#262627"
            >
              New Components
            </Typography>
          </Grid>
          {/* <RetirementGap /> */}
          {/* <AltruistIndex /> */}
          {/* <DailyUserCard /> */}
          <Grid container>
            {/* <Grid item xs={12} md={6} sx={{ margin: 'auto' }}>
              <QuilttConnectProvider />
            </Grid> */}
            {/* <Grid item xs={12} md={6} sx={{ margin: 'auto' }}>
              <SignalConnection></SignalConnection>
            </Grid> */}

            {/* <Grid item xs={12}>
              Test Audit Trail
              <AuditTimeline userId={intakeState?.id} />
            </Grid> */}

            <Grid item xs={12}></Grid>

            {/* <Grid item xs={12} md={6} sx={{ margin: 'auto' }}>
              <ReferralModal
                handleClose={handleReferralClose}
                open={openReferral}
              />
              Sidenav Button Referral
              <Card
                sx={{
                  overflow: 'visible',
                  textAlign: 'left',
                  boxShadow: 0,
                  borderRadius: 2,
                  border: '1px solid #D2D6DA',
                  p: 2,
                  mb: 6,
                  backgroundColor: '#344767',
                }}
              >
                <Box>
                  <Button
                    rel="noreferrer"
                    fullWidth
                    startIcon={<GifBoxOutlined />}
                    onClick={() => openModal('referral')}
                    sx={{
                      whiteSpace: 'normal',
                      backgroundColor: 'transparent !important',
                      letterSpacing: '0.25px',
                      boxShadow: 'none',
                      color: '#fff',
                      borderRadius: '4px',
                      padding: '10px',
                      fontSize: '18px',
                      fontWeight: 'normal',
                      textTransform: 'capitalize',
                      paddingLeft: '20px',
                      justifyContent: 'flex-start',
                      '&:hover, &:active, &:focus, &:focus:not(:hover)': {
                        transform: 'scale(1.0)',
                        backgroundColor: `${colors.parco.buttons.transparentSidebar.hover} !important`,
                        color:
                          colors.parco.buttons.transparentSidebar.textHover,
                      },
                    }}
                  >
                    Refer a Friend
                  </Button>
                </Box>
              </Card>
            </Grid> */}
            <Grid item xs={12} md={6} sx={{ margin: 'auto' }}>
              <WebinarSignup />
            </Grid>
            <Grid item xs={12}></Grid>
            <Grid item xs={12} md={6} sx={{ margin: 'auto' }}>
              Onboarding Card Referral
              <Card
                sx={{
                  overflow: 'visible',
                  textAlign: 'left',
                  boxShadow: 0,
                  borderRadius: 2,
                  border: '1px solid #D2D6DA',
                  p: 2,
                  mb: 6,
                }}
              >
                <Referral />
              </Card>
            </Grid>
            <Grid item xs={12}></Grid>

            <Grid item xs={12} md={6} sx={{ margin: 'auto' }}>
              Appointment Schedule Basics
              <Card
                sx={{
                  overflow: 'visible',
                  textAlign: 'left',
                  boxShadow: 0,
                  borderRadius: 2,
                  border: '1px solid #D2D6DA',
                  p: 2,
                  mb: 6,
                }}
              >
                <AppointmentScheduler isAdmin={isAdmin} />
              </Card>
            </Grid>
            <Grid item xs={12}></Grid>
            <Grid item xs={12} md={6} sx={{ margin: 'auto' }}>
              <Button
                size="large"
                variant="contained"
                onClick={() =>
                  openModal('scheduleAppointment', { isAdmin: true })
                }
                sx={{
                  fontSize: '15px',
                  padding: '12px 24px',
                  backgroundColor: '#344767',
                  color: '#fff',
                  fontWeight: 'normal',
                  boxShadow: 'none',
                  textTransform: 'capitalize',
                  letterSpacing: 0.5,
                  borderRadius: '6px',
                  mb: 6,
                  '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                    transform: 'none',
                    backgroundColor: '#293852',
                    boxShadow: 'none',
                  },
                }}
              >
                Test Modal Context Provider - Schedule Appointment
              </Button>
            </Grid>
            <Grid item xs={12}></Grid>

            <Grid item xs={12} md={6} sx={{ margin: 'auto' }}>
              <Button
                size="large"
                variant="contained"
                onClick={() =>
                  openModal('fieldModal', {
                    title: `Let's update your Thrift Savings Plan (Traditional) Balance!`,
                    userField: 'tspBalance',
                    inputType: 'currencyFloat',
                    fieldLabel: 'Thrift Savings Plan (Traditional) Balance',
                  })
                }
                sx={{
                  fontSize: '15px',
                  padding: '12px 24px',
                  backgroundColor: '#344767',
                  color: '#fff',
                  fontWeight: 'normal',
                  boxShadow: 'none',
                  textTransform: 'capitalize',
                  letterSpacing: 0.5,
                  borderRadius: '6px',
                  mb: 6,
                  '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                    transform: 'none',
                    backgroundColor: '#293852',
                    boxShadow: 'none',
                  },
                }}
              >
                Update FIELD Modal
              </Button>
            </Grid>
            <Grid item xs={12}></Grid>

            <Grid item xs={12} md={6} sx={{ margin: 'auto' }}>
              <Button
                size="large"
                variant="contained"
                onClick={() =>
                  openModal('addLifeInsurance', {
                    intakeState: intakeState,
                    updateIntakeStateAndSubmit: updateIntakeStateAndSubmit,
                    updateIntakeStateAtKey: updateIntakeStateAtKey,
                  })
                }
                sx={{
                  fontSize: '15px',
                  padding: '12px 24px',
                  backgroundColor: '#344767',
                  color: '#fff',
                  fontWeight: 'normal',
                  boxShadow: 'none',
                  textTransform: 'capitalize',
                  letterSpacing: 0.5,
                  borderRadius: '6px',
                  mb: 6,
                  '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                    transform: 'none',
                    backgroundColor: '#293852',
                    boxShadow: 'none',
                  },
                }}
              >
                Test Modal Context Provider - Life Insurance
              </Button>
            </Grid>
            <Grid item xs={12}></Grid>

            <Grid item xs={12} md={6} sx={{ margin: 'auto' }}>
              <Button
                size="large"
                variant="contained"
                onClick={() =>
                  openModal('annuityRecommender', {
                    intakeState: intakeState,
                    updateIntakeStateAndSubmit: updateIntakeStateAndSubmit,
                    updateIntakeStateAtKey: updateIntakeStateAtKey,
                  })
                }
                sx={{
                  fontSize: '15px',
                  padding: '12px 24px',
                  backgroundColor: '#344767',
                  color: '#fff',
                  fontWeight: 'normal',
                  boxShadow: 'none',
                  textTransform: 'capitalize',
                  letterSpacing: 0.5,
                  borderRadius: '6px',
                  mb: 6,
                  '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                    transform: 'none',
                    backgroundColor: '#293852',
                    boxShadow: 'none',
                  },
                }}
              >
                Test Modal Context Provider - Income GAP
              </Button>
            </Grid>
            <Grid item xs={12}></Grid>

            <Grid item xs={12} md={6} sx={{ margin: 'auto' }}>
              <Button
                size="large"
                variant="contained"
                onClick={() =>
                  openModal('addLifeInsurance', {
                    intakeState: intakeState,
                    updateIntakeStateAndSubmit: updateIntakeStateAndSubmit,
                    updateIntakeStateAtKey: updateIntakeStateAtKey,
                  })
                }
                sx={{
                  fontSize: '15px',
                  padding: '12px 24px',
                  backgroundColor: '#344767',
                  color: '#fff',
                  fontWeight: 'normal',
                  boxShadow: 'none',
                  textTransform: 'capitalize',
                  letterSpacing: 0.5,
                  borderRadius: '6px',
                  mb: 6,
                  '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                    transform: 'none',
                    backgroundColor: '#293852',
                    boxShadow: 'none',
                  },
                }}
              >
                Test Modal Context Provider - Roth IRA
              </Button>
            </Grid>
            <Grid item xs={12}></Grid>

            <Grid item xs={12} md={6} sx={{ margin: 'auto' }}>
              <Button
                size="large"
                variant="contained"
                onClick={() =>
                  openModal('addLifeInsurance', {
                    intakeState: intakeState,
                    updateIntakeStateAndSubmit: updateIntakeStateAndSubmit,
                    updateIntakeStateAtKey: updateIntakeStateAtKey,
                  })
                }
                sx={{
                  fontSize: '15px',
                  padding: '12px 24px',
                  backgroundColor: '#344767',
                  color: '#fff',
                  fontWeight: 'normal',
                  boxShadow: 'none',
                  textTransform: 'capitalize',
                  letterSpacing: 0.5,
                  borderRadius: '6px',
                  mb: 6,
                  '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                    transform: 'none',
                    backgroundColor: '#293852',
                    boxShadow: 'none',
                  },
                }}
              >
                Test Modal Context Provider - Trust & Will
              </Button>
            </Grid>
            <Grid item xs={12}></Grid>

            <Grid item xs={12} md={6} sx={{ margin: 'auto' }}>
              <Button
                size="large"
                variant="contained"
                onClick={() =>
                  openModal('willSimple', {
                    updateIntakeStateAndSubmit: updateIntakeStateAndSubmit,
                    updateIntakeStateAtKey: updateIntakeStateAtKey,
                  })
                }
                sx={{
                  fontSize: '15px',
                  padding: '12px 24px',
                  backgroundColor: '#344767',
                  color: '#fff',
                  fontWeight: 'normal',
                  boxShadow: 'none',
                  textTransform: 'capitalize',
                  letterSpacing: 0.5,
                  borderRadius: '6px',
                  mb: 6,
                  '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                    transform: 'none',
                    backgroundColor: '#293852',
                    boxShadow: 'none',
                  },
                }}
              >
                Test Modal Context Provider - Will Simple
              </Button>
            </Grid>
            <Grid item xs={12}></Grid>

            <Grid item xs={12} md={6} sx={{ margin: 'auto' }}>
              <Button
                size="large"
                variant="contained"
                onClick={() =>
                  openModal('willSimple', {
                    addTrust: true,
                    updateIntakeStateAndSubmit: updateIntakeStateAndSubmit,
                    updateIntakeStateAtKey: updateIntakeStateAtKey,
                  })
                }
                sx={{
                  fontSize: '15px',
                  padding: '12px 24px',
                  backgroundColor: '#344767',
                  color: '#fff',
                  fontWeight: 'normal',
                  boxShadow: 'none',
                  textTransform: 'capitalize',
                  letterSpacing: 0.5,
                  borderRadius: '6px',
                  mb: 6,
                  '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                    transform: 'none',
                    backgroundColor: '#293852',
                    boxShadow: 'none',
                  },
                }}
              >
                Test Modal Context Provider - Trust Simple
              </Button>
            </Grid>
            <Grid item xs={12}></Grid>

            <Grid item xs={12} md={6} sx={{ margin: 'auto' }}>
              <Button
                size="large"
                variant="contained"
                onClick={() => openModal('questionModal')}
                sx={{
                  fontSize: '15px',
                  padding: '12px 24px',
                  backgroundColor: '#344767',
                  color: '#fff',
                  fontWeight: 'normal',
                  boxShadow: 'none',
                  textTransform: 'capitalize',
                  letterSpacing: 0.5,
                  borderRadius: '6px',
                  mb: 6,
                  '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                    transform: 'none',
                    backgroundColor: '#293852',
                    boxShadow: 'none',
                  },
                }}
              >
                Test Modal Context Provider - Question Modal
              </Button>
            </Grid>
            <Grid item xs={12}></Grid>

            <Grid item xs={12} md={6} sx={{ margin: 'auto' }}>
              <Button
                size="large"
                variant="contained"
                onClick={() => openModal('fallbackModal')}
                sx={{
                  fontSize: '15px',
                  padding: '12px 24px',
                  backgroundColor: '#344767',
                  color: '#fff',
                  fontWeight: 'normal',
                  boxShadow: 'none',
                  textTransform: 'capitalize',
                  letterSpacing: 0.5,
                  borderRadius: '6px',
                  mb: 6,
                  '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                    transform: 'none',
                    backgroundColor: '#293852',
                    boxShadow: 'none',
                  },
                }}
              >
                Test Modal Context Provider - Fallback Test
              </Button>
            </Grid>
            <Grid item xs={12}></Grid>

            <Grid item xs={12} md={6} sx={{ margin: 'auto' }}>
              <Card
                sx={{
                  overflow: 'visible',
                  textAlign: 'left',
                  boxShadow: 0,
                  borderRadius: 2,
                  border: '1px solid #D2D6DA',
                  p: 2,
                  mb: 12,
                }}
              >
                <Grid container columnSpacing={3} rowSpacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h5" mb={1}>
                      PARCO Score Calculator
                    </Typography>

                    <Typography variant="h6" mt={2}>
                      Base Inputs:
                    </Typography>
                    <Typography variant="body2">
                      Net Take Home (Working):{' '}
                      {dollarFormat.format(adjustedWorkingNetTakeHome)} (
                      {dollarFormat.format(adjustedWorkingNetTakeHome * 12)})
                    </Typography>
                    <Typography variant="body2">
                      Net Take Home (Retirement):{' '}
                      {dollarFormat.format(adjustedNetTakeHomeInRetirement)} (
                      {dollarFormat.format(
                        adjustedNetTakeHomeInRetirement * 12
                      )}
                      )
                    </Typography>
                    <Typography variant="body2">
                      Gap:{' '}
                      {dollarFormat.format(
                        adjustedNetTakeHomeDifferencePARCOGap
                      )}
                    </Typography>
                    <Typography variant="body2">
                      Current Age:{' '}
                      {Math.round(
                        differenceInMonths(
                          new Date(),
                          new Date(intakeState?.dateOfBirth)
                        ) / 12
                      )}
                    </Typography>
                    <Typography variant="body2">
                      Retirement Age:{' '}
                      {Math.round(
                        differenceInMonths(
                          new Date(intakeState?.dateOfPlannedRetirement),
                          new Date(intakeState?.dateOfBirth)
                        ) / 12
                      )}
                    </Typography>
                    <Typography variant="body2">
                      Deferral Years (Rounded Down):{' '}
                      {Math.floor(
                        differenceInMonths(
                          new Date(intakeState?.dateOfPlannedRetirement),
                          new Date()
                        ) / 12
                      )}
                    </Typography>
                    <Typography variant="body2">
                      Tax Rate:{' '}
                      {Math.round((intakeState?.taxRate || 20) * 100) / 100}%
                    </Typography>

                    <Typography variant="h6" mt={2}>
                      Annuity Ammounts Calculated:
                    </Typography>
                    <Grid container columnSpacing={3} rowSpacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="h6">Level:</Typography>
                        <Typography variant="body2">
                          Payout: {naReference.payoutRatio * 100}%
                        </Typography>
                        <Typography variant="body2">
                          Capital Needed:{' '}
                          {dollarFormat.format(naReference.cost)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="h6">
                          Inflation Adjusted:
                        </Typography>
                        <Typography variant="body2">
                          Payout: {fngReference.payoutRatio * 100}%
                        </Typography>
                        <Typography variant="body2">
                          Capital Needed:{' '}
                          {dollarFormat.format(fngReference.cost)}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Typography variant="h6" mt={2}>
                      Investable Assets:
                    </Typography>
                    <Typography variant="body2">
                      Total Investable: {dollarFormat.format(valueOfAssets)}
                    </Typography>
                    <Typography variant="body2">
                      Total Available for FIA (75%):{' '}
                      {dollarFormat.format(valueOfAssets * 0.75)}
                    </Typography>
                    <Typography variant="body2">
                      Net From 4% Rule:{' '}
                      {dollarFormat.format(
                        valueOfAssets *
                          0.75 *
                          0.04 *
                          (1 - (intakeState?.taxRate || 20) / 100)
                      )}
                    </Typography>
                    <Typography variant="h6" mt={2}>
                      Income with 4% Rule:
                    </Typography>
                    <Typography variant="body2">
                      Net From 4% Rule:{' '}
                      {dollarFormat.format(
                        valueOfAssets *
                          0.75 *
                          0.04 *
                          (1 - (intakeState?.taxRate || 20) / 100)
                      )}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ borderBottom: '1px solid #ddd' }}
                    >
                      Net Take Home (Retirement):{' '}
                      {dollarFormat.format(
                        adjustedNetTakeHomeInRetirement * 12
                      )}
                    </Typography>
                    <Typography variant="body2">
                      Net Take Home (Retirement + 4%):{' '}
                      {dollarFormat.format(
                        adjustedNetTakeHomeInRetirement * 12 +
                          valueOfAssets *
                            0.75 *
                            0.04 *
                            (1 - (intakeState?.taxRate || 20) / 100)
                      )}
                    </Typography>
                    <Typography variant="body2">
                      Net Take Home (Working):{' '}
                      {dollarFormat.format(adjustedWorkingNetTakeHome * 12)}
                    </Typography>
                    <Typography variant="body2">
                      Base PARCO Score 4% Rule:{' '}
                      {((adjustedNetTakeHomeInRetirement * 12 +
                        valueOfAssets *
                          0.75 *
                          0.04 *
                          (1 - (intakeState?.taxRate || 20) / 100)) /
                        (adjustedWorkingNetTakeHome * 12)) *
                        100}
                    </Typography>

                    <Typography variant="h6" mt={2}>
                      Income with Annuity:
                    </Typography>
                    <Grid container columnSpacing={3} rowSpacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="h6">
                          Level:{' '}
                          {dollarFormat.format(
                            valueOfAssets * 0.75 * naReference.payoutRatio
                          )}
                        </Typography>
                        <Typography variant="body2">
                          Net Payout:{' '}
                          {dollarFormat.format(
                            valueOfAssets *
                              0.75 *
                              naReference.payoutRatio *
                              (1 - (intakeState?.taxRate || 20) / 100)
                          )}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ borderBottom: '1px solid #ddd' }}
                        >
                          Net Take Home (Retirement):{' '}
                          {dollarFormat.format(
                            adjustedNetTakeHomeInRetirement * 12
                          )}
                        </Typography>
                        <Typography variant="body2">
                          Net Combined:{' '}
                          {dollarFormat.format(
                            valueOfAssets *
                              0.75 *
                              naReference.payoutRatio *
                              (1 - (intakeState?.taxRate || 20) / 100) +
                              adjustedNetTakeHomeInRetirement * 12
                          )}
                        </Typography>
                        <Typography variant="body2">
                          Net Take Home (Working):{' '}
                          {dollarFormat.format(adjustedWorkingNetTakeHome * 12)}
                        </Typography>
                        <Typography variant="body2">
                          PARCO Score Level:{' '}
                          {((valueOfAssets *
                            0.75 *
                            naReference.payoutRatio *
                            (1 - (intakeState?.taxRate || 20) / 100) +
                            adjustedNetTakeHomeInRetirement * 12) /
                            (adjustedWorkingNetTakeHome * 12)) *
                            100}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="h6">
                          Inflation Adjusted:{' '}
                          {dollarFormat.format(
                            valueOfAssets * 0.75 * fngReference.payoutRatio
                          )}
                        </Typography>
                        <Typography variant="body2">
                          Net Payout:{' '}
                          {dollarFormat.format(
                            valueOfAssets *
                              0.75 *
                              fngReference.payoutRatio *
                              (1 - (intakeState?.taxRate || 20) / 100)
                          )}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ borderBottom: '1px solid #ddd' }}
                        >
                          Net Take Home (Retirement):{' '}
                          {dollarFormat.format(
                            adjustedNetTakeHomeInRetirement * 12
                          )}
                        </Typography>
                        <Typography variant="body2">
                          Net Combined:{' '}
                          {dollarFormat.format(
                            valueOfAssets *
                              0.75 *
                              fngReference.payoutRatio *
                              (1 - (intakeState?.taxRate || 20) / 100) +
                              adjustedNetTakeHomeInRetirement * 12
                          )}
                        </Typography>
                        <Typography variant="body2">
                          Net Take Home (Working):{' '}
                          {dollarFormat.format(adjustedWorkingNetTakeHome * 12)}
                        </Typography>
                        <Typography variant="body2">
                          PARCO Score Inflation Adjusted:{' '}
                          {((valueOfAssets *
                            0.75 *
                            fngReference.payoutRatio *
                            (1 - (intakeState?.taxRate || 20) / 100) +
                            adjustedNetTakeHomeInRetirement * 12) /
                            (adjustedWorkingNetTakeHome * 12)) *
                            100}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
            <Grid item xs={12}></Grid>
            <Grid item xs={12} md={6} sx={{ margin: 'auto' }}>
              <Card
                sx={{
                  overflow: 'visible',
                  textAlign: 'left',
                  boxShadow: 0,
                  borderRadius: 2,
                  border: '1px solid #D2D6DA',
                  p: 2,
                  mb: 12,
                }}
              >
                <Grid container columnSpacing={3} rowSpacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h5" mb={1}>
                      PARCO Annuity Calculator
                    </Typography>

                    <Typography variant="h6" mt={2}>
                      Base Inputs:
                    </Typography>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </DashboardLayout>
    </AltruistOpenRothProvider>
    // </RetirementGapProvider>
  )
}
